import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAccess } from "@/lib/adminAccess";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  getAdminDashboard, getLeads, getDealerApplications,
  getAnalyticsSummary, getDealerSignups, getTopPages,
  getApplicationFunnel, getMostViewedPages, getStatePageRankings,
  getApplicationAttributionSummary,
} from "@/lib/api";
import { toast } from "sonner";
import { VerizonSalesSnapshotCard } from "@/components/verizon/VerizonSalesSnapshotCard";
import { getVzAdminSummary } from "@/lib/verizonSalesApi";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import {
  Users, UserPlus, FileText, Eye, TrendingUp, TrendingDown,
  Clock, Loader2, ArrowRight, RefreshCcw,
  Package, DollarSign, CheckCircle, MapPin, BarChart3, Map, Target,
} from "lucide-react";

const NUMBER_FMT = new Intl.NumberFormat("en-US");

function rangeToDays(range) {
  if (range === "mtd") {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return Math.max(1, Math.ceil((now - firstOfMonth) / 86400000) + 1);
  }
  return Number(range);
}

function KpiCard({ icon: Icon, label, value, deltaPct, accent = "teal", testId, alert = false }) {
  const hasDelta = deltaPct != null;
  const positive = hasDelta && deltaPct >= 0;
  return (
    <Card className={alert ? "ring-2 ring-yellow-400" : ""} data-testid={testId}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium truncate">{label}</p>
            <p className="text-2xl font-heading font-bold text-navy mt-1">
              {NUMBER_FMT.format(value ?? 0)}
            </p>
            {hasDelta && (
              <p className={`text-xs font-medium mt-1 flex items-center gap-1 ${positive ? "text-emerald-600" : "text-red-600"}`}>
                {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(deltaPct)}% vs prior 30d
              </p>
            )}
          </div>
          <div className={`bg-${accent}/10 p-2 rounded-md shrink-0`}>
            <Icon className={`w-5 h-5 text-${accent}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-teal" />
    </div>
  );
}

export default function AdminDashboard() {
  // ---- Dashboard legacy state ----
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [pendingApps, setPendingApps] = useState([]);
  const [loadingDash, setLoadingDash] = useState(true);

  // ---- Analytics state ----
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [summary, setSummary] = useState(null);
  const [signupsBucket, setSignupsBucket] = useState("day");
  const [signupsDays, setSignupsDays] = useState(90);
  const [signups, setSignups] = useState(null);
  const [topPagesDays, setTopPagesDays] = useState(30);
  const [topPages, setTopPages] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [mostViewedRange, setMostViewedRange] = useState("mtd");
  const [mostViewed, setMostViewed] = useState(null);
  const [stateRankingsDays, setStateRankingsDays] = useState(30);
  const [stateRankings, setStateRankings] = useState(null);
  const [showAllStates, setShowAllStates] = useState(false);
  const [attribution, setAttribution] = useState(null);

  const { can } = useAdminAccess();
  const loadAll = async () => {
    setLoadingDash(true);
    setLoadingAnalytics(true);
    try {
      const [dashData, leadsData, appsData, s, du, tp, fn, mv, sr, attr] = await Promise.all([
        getAdminDashboard().catch(() => null),
        can("leads") ? getLeads().catch(() => []) : [],
        can("applications") ? getDealerApplications("pending").catch(() => []) : [],
        getAnalyticsSummary().catch(() => null),
        getDealerSignups(signupsDays, signupsBucket).catch(() => null),
        getTopPages(topPagesDays, 10).catch(() => null),
        getApplicationFunnel().catch(() => null),
        getMostViewedPages(rangeToDays(mostViewedRange), 15).catch(() => null),
        getStatePageRankings(stateRankingsDays).catch(() => null),
        can("applications") ? getApplicationAttributionSummary(10).catch(() => null) : null,
      ]);
      setStats(dashData);
      setRecentLeads((leadsData || []).slice(0, 5));
      setPendingApps((appsData || []).slice(0, 5));
      setSummary(s);
      setSignups(du);
      setTopPages(tp);
      setFunnel(fn);
      setMostViewed(mv);
      setStateRankings(sr);
      setAttribution(attr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoadingDash(false);
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await getDealerSignups(signupsDays, signupsBucket);
        setSignups(data);
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, [signupsBucket, signupsDays]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTopPages(topPagesDays, 10);
        setTopPages(data);
      } catch (e) { /* silent */ }
    })();
  }, [topPagesDays]);

  useEffect(() => {
    (async () => {
      try { setMostViewed(await getMostViewedPages(rangeToDays(mostViewedRange), 15)); } catch (e) { /* silent */ }
    })();
  }, [mostViewedRange]);

  useEffect(() => {
    (async () => {
      try { setStateRankings(await getStatePageRankings(stateRankingsDays)); } catch (e) { /* silent */ }
    })();
  }, [stateRankingsDays]);

  // Combined KPI cards (richer set from both pages)
  const kpiCards = [
    { title: "Total Dealers", value: summary?.total_dealers ?? stats?.total_dealers, icon: Users, delta: summary?.new_dealers_delta_pct, testId: "admin-kpi-total-dealers" },
    { title: "New Applications (30d)", value: summary?.new_applications_30d, icon: UserPlus, testId: "admin-kpi-new-apps" },
    { title: "New Leads (30d)", value: summary?.new_leads_30d, icon: FileText, testId: "admin-kpi-new-leads" },
    { title: "Pageviews (30d)", value: summary?.pageviews_30d, icon: Eye, testId: "admin-kpi-pageviews" },
    { title: "Pending Applicants", value: stats?.pending_applicants, icon: Package, alert: (stats?.pending_applicants || 0) > 0, testId: "admin-kpi-pending" },
    { title: "Vetting", value: stats?.vetting_applicants, icon: DollarSign, testId: "admin-kpi-vetting" },
    { title: "Pre-Approval", value: stats?.pre_approval_applicants, icon: Clock, testId: "admin-kpi-pre-approval" },
    { title: "Total Locations", value: summary?.total_dealers ? stats?.total_locations : stats?.total_locations, icon: MapPin, testId: "admin-kpi-locations" },
  ];

  const signupsChartData = useMemo(
    () => (signups?.points || []).map((p) => ({ bucket: p.bucket, count: p.count })),
    [signups]
  );
  const topPagesChartData = useMemo(
    () => (topPages?.rows || []).map((r) => ({
      path: r.path,
      Views: r.views,
      Leads: r.leads,
      Applications: r.applications,
    })),
    [topPages]
  );
  const funnelRows = funnel?.stages || [];

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Top bar with refresh */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-slate-600">
            Operations snapshot plus live analytics for dealers, landing pages, the applicant funnel and email campaigns.
          </p>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loadingDash || loadingAnalytics} data-testid="dashboard-refresh-btn">
            <RefreshCcw className={`w-4 h-4 mr-1 ${(loadingDash || loadingAnalytics) ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpiCards.map((k) => (
            <KpiCard
              key={k.title}
              icon={k.icon}
              label={k.title}
              value={k.value}
              deltaPct={k.delta}
              alert={k.alert}
              testId={k.testId}
            />
          ))}
        </div>

        {can("dealer_sales") && <VerizonSalesSnapshotCard fetcher={getVzAdminSummary} link="/admin/verizon-sales" testId="admin-vz-snapshot" />}

        {/* Dealer Signups Line Chart */}
        <Card data-testid="dealer-signups-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-teal" /> Dealer Signups Over Time
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={signupsBucket} onValueChange={setSignupsBucket}>
                <SelectTrigger className="h-8 w-28 text-xs" data-testid="signups-bucket"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(signupsDays)} onValueChange={(v) => setSignupsDays(Number(v))}>
                <SelectTrigger className="h-8 w-28 text-xs" data-testid="signups-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loadingAnalytics || !signups ? <Spinner /> : (
              <>
                <div className="text-xs text-slate-500 mb-2">
                  <b>{signups.total_new_dealers}</b> new dealers in the selected range.
                </div>
                <div style={{ width: "100%", height: 260, minHeight: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={signupsChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" fontSize={11} tick={{ fill: "#475569" }} />
                      <YAxis allowDecimals={false} fontSize={11} tick={{ fill: "#475569" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelStyle={{ color: "#0f172a" }} />
                      <Line type="monotone" dataKey="count" name="New dealers" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pending Applications + Quick Actions */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-navy text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-yellow-500" />
                Pending Applications
              </CardTitle>
              <Link to="/admin/applications">
                <Button variant="ghost" size="sm" className="text-teal">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loadingDash ? (
                <p className="text-slate-500 text-center py-4 font-body">Loading...</p>
              ) : pendingApps.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-slate-500 font-body">No pending applications</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingApps.map((app) => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-navy font-body">{app.first_name} {app.last_name}</p>
                        <p className="text-sm text-slate-500 font-body">{app.company_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500 font-body">{new Date(app.created_at).toLocaleDateString()}</p>
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pending</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-navy text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/admin/applications">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-applications">
                  <span>Review Applications</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/leads">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-leads">
                  <span>Manage Leads</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/dealers">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-dealers">
                  <span>View Dealers</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/announcements">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-announcements">
                  <span>Announcements</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/locations">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-locations">
                  <span>Manage Locations</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/reports/audit-log">
                <Button variant="outline" className="w-full justify-between" data-testid="admin-quick-audit">
                  <span>Audit Log</span><ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Most Viewed Pages (all public pages) */}
        <Card data-testid="most-viewed-pages-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-teal" /> Most Viewed Pages
            </CardTitle>
            <Select value={mostViewedRange} onValueChange={setMostViewedRange}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="most-viewed-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mtd">Month to Date</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loadingAnalytics || !mostViewed ? <Spinner /> : (
              <>
                <p className="text-xs text-slate-500 mb-2">
                  Ranked by pageviews across the entire public site. Total views in range: <b>{NUMBER_FMT.format(mostViewed.total_views)}</b>.
                </p>
                {(mostViewed.rows || []).length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No pageviews recorded yet in this range.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="most-viewed-table">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-slate-600">
                          <th className="py-2 pr-3 font-medium w-10">#</th>
                          <th className="py-2 pr-3 font-medium">Page</th>
                          <th className="py-2 pr-3 font-medium text-right">Views</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mostViewed.rows.slice(0, 5).map((r, idx) => (
                          <tr key={r.path} className="border-b border-slate-100 hover:bg-slate-50/60">
                            <td className="py-2 pr-3 text-slate-500">{idx + 1}</td>
                            <td className="py-2 pr-3">
                              <a href={r.path} target="_blank" rel="noreferrer" className="font-medium text-navy hover:text-teal hover:underline">
                                {r.label}
                              </a>
                            </td>
                            <td className="py-2 pr-3 text-right font-bold text-navy tabular-nums">
                              {NUMBER_FMT.format(r.views)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* All 50 States Ranking */}
        <Card data-testid="state-rankings-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Map className="w-4 h-4 text-teal" /> State Landing Pages — All 50 Ranked
            </CardTitle>
            <Select value={String(stateRankingsDays)} onValueChange={(v) => setStateRankingsDays(Number(v))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="state-rankings-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loadingAnalytics || !stateRankings ? <Spinner /> : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-3 text-center">
                  <div className="p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-500">Landing Views</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(stateRankings.totals?.landing || 0)}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-500">Store Page Views</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(stateRankings.totals?.locations || 0)}</p>
                  </div>
                  <div className="p-2 bg-teal/10 rounded-md">
                    <p className="text-xs text-teal">Total Views</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(stateRankings.totals?.total || 0)}</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="state-rankings-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="py-2 pr-3 font-medium w-12">Rank</th>
                        <th className="py-2 pr-3 font-medium">State</th>
                        <th className="py-2 pr-3 font-medium text-right">Landing</th>
                        <th className="py-2 pr-3 font-medium text-right">Store Pages</th>
                        <th className="py-2 pr-3 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllStates ? stateRankings.states : stateRankings.states.slice(0, 10)).map((s) => (
                        <tr key={s.slug} className={`border-b border-slate-100 hover:bg-slate-50/60 ${s.total_views === 0 ? "text-slate-400" : ""}`}>
                          <td className="py-2 pr-3 tabular-nums">#{s.rank}</td>
                          <td className="py-2 pr-3">
                            <a href={s.path} target="_blank" rel="noreferrer" className="font-medium hover:text-teal hover:underline">
                              {s.name}
                            </a>
                            <span className="ml-1 text-xs text-slate-400">({s.abbr})</span>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{NUMBER_FMT.format(s.landing_views)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{NUMBER_FMT.format(s.location_views)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums font-bold text-navy">{NUMBER_FMT.format(s.total_views)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {stateRankings.states.length > 10 && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllStates((v) => !v)}
                      data-testid="toggle-all-states-btn"
                    >
                      {showAllStates ? "Show Top 10 Only" : `Show All 50 States`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Top State Pages Bar Chart */}
        <Card data-testid="top-pages-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-teal" /> Landing Page Performance Score (Apps + Leads + Views)
            </CardTitle>
            <Select value={String(topPagesDays)} onValueChange={(v) => setTopPagesDays(Number(v))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="top-pages-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loadingAnalytics || !topPages ? <Spinner /> : (
              <>
                <div className="text-xs text-slate-500 mb-2">
                  Ranked by: applications × 10 + leads × 3 + views × 0.1. Includes <code>/totalwirelessmasteragent</code> and all state slugs (<code>/michigan</code>, <code>/florida</code>, etc.).
                </div>
                {topPagesChartData.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No activity recorded yet in this range.</p>
                ) : (
                  <div style={{ width: "100%", height: Math.max(260, topPagesChartData.length * 40), minHeight: 260 }}>
                    <ResponsiveContainer>
                      <BarChart layout="vertical" data={topPagesChartData} margin={{ left: 30, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" fontSize={11} tick={{ fill: "#475569" }} />
                        <YAxis type="category" dataKey="path" fontSize={11} tick={{ fill: "#475569" }} width={140} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Applications" fill="#0d9488" />
                        <Bar dataKey="Leads" fill="#3b82f6" />
                        <Bar dataKey="Views" fill="#94a3b8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-500">Total Views</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(topPages.totals?.views || 0)}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-500">Total Leads</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(topPages.totals?.leads || 0)}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-500">Total Applications</p>
                    <p className="text-lg font-bold text-navy">{NUMBER_FMT.format(topPages.totals?.applications || 0)}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Application Funnel Timing */}
        <Card data-testid="application-funnel-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal" /> Application Funnel Timing
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAnalytics || !funnel ? <Spinner /> : (
              <>
                <p className="text-sm text-slate-600 mb-4">
                  Average time spent in each stage. Based on <b>{funnel.total_applications}</b> total applications;
                  <b> {funnel.completed_count}</b> fully completed to Dealer in avg <b>{funnel.avg_total_days_to_dealer}</b> days.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="funnel-stages-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="py-2 pr-3 font-medium">Stage</th>
                        <th className="py-2 pr-3 font-medium">Currently In Stage</th>
                        <th className="py-2 pr-3 font-medium">Avg Time in Stage</th>
                        <th className="py-2 pr-3 font-medium">Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnelRows.map((s, idx) => (
                        <tr key={s.stage} className="border-b border-slate-100">
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-teal/10 text-teal text-xs font-bold">
                                {idx + 1}
                              </span>
                              <span className="font-medium text-slate-800">{s.label}</span>
                              {idx < funnelRows.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 ml-2" />}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-700">{s.current_count}</td>
                          <td className="py-2 pr-3 text-slate-700">
                            {s.samples > 0 ? `${s.avg_days} days (${s.avg_hours.toFixed(1)} hrs)` : <span className="text-slate-400 italic">—</span>}
                          </td>
                          <td className="py-2 pr-3 text-slate-500">{s.samples}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Timing is captured each time an admin moves an application between stages.
                  Older applications pre-dating this tracking will show as having 0 samples.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-navy text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" /> Recent Leads
            </CardTitle>
            <Link to="/admin/leads">
              <Button variant="ghost" size="sm" className="text-teal">View All <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loadingDash ? (
              <p className="text-slate-500 text-center py-4 font-body">Loading...</p>
            ) : recentLeads.length === 0 ? (
              <p className="text-slate-500 text-center py-6 font-body">No leads yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</th>
                      <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</th>
                      <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Source</th>
                      <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 px-2 font-medium text-navy font-body">{lead.name}</td>
                        <td className="py-3 px-2 text-sm text-slate-600 font-body">{lead.email}</td>
                        <td className="py-3 px-2 text-sm text-slate-500 font-body capitalize">{(lead.source || "").replace(/_/g, ' ')}</td>
                        <td className="py-3 px-2 text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            lead.status === "new" ? "bg-blue-100 text-blue-700"
                              : lead.status === "contacted" ? "bg-yellow-100 text-yellow-700"
                              : lead.status === "converted" ? "bg-green-100 text-green-700"
                              : "bg-slate-100 text-slate-700"
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top-Converting SEO Pages — dealer application attribution */}
        <Card className="border-slate-200" data-testid="dashboard-attribution-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-navy text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-teal" /> Top-Converting SEO Pages
            </CardTitle>
            <span className="text-xs text-slate-500 font-body">
              {attribution
                ? `${attribution.applications_with_attribution}/${attribution.total_applications} applications attributed`
                : "—"}
            </span>
          </CardHeader>
          <CardContent>
            {loadingDash ? (
              <p className="text-slate-500 text-center py-4 font-body">Loading...</p>
            ) : !attribution || !attribution.top_pages || attribution.top_pages.length === 0 ? (
              <p className="text-slate-500 text-center py-6 font-body text-sm">
                No attributed applications yet. Data appears once new applications arrive with first-touch landing-page tracking.
              </p>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Top Landing Pages</p>
                  <table className="w-full" data-testid="dashboard-attribution-pages">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Page</th>
                        <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Apps</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attribution.top_pages.map((row) => (
                        <tr key={row.pathname} className="border-b border-slate-100 last:border-0">
                          <td className="py-2 font-mono text-xs text-navy truncate max-w-[240px]" title={row.pathname}>{row.pathname}</td>
                          <td className="py-2 text-right font-semibold text-teal">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">UTM Sources</p>
                  {attribution.top_utm_sources && attribution.top_utm_sources.length > 0 ? (
                    <table className="w-full" data-testid="dashboard-attribution-utm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Source</th>
                          <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Apps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attribution.top_utm_sources.map((row) => (
                          <tr key={row.source} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 text-sm text-navy capitalize">{row.source}</td>
                            <td className="py-2 text-right font-semibold text-teal">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-slate-400 text-sm italic font-body py-2">No UTM-tagged traffic yet.</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
}
